import React, {useState, useEffect, useMemo} from 'react';
import {Modal, Row, Col, Tab, Tabs} from 'react-bootstrap';
import {Button, Badge} from '@/components/ui';
import {Icon} from '@/components/icons/Icon';
import {useAlert} from '@/components/alerts';
import axios from 'axios';
import i18n from 'i18n';
import {buildURL} from '@/utils/urlBuilder';
import Spinner from '@/components/ui/Spinner';
import TableComponent from '@/components/table/Table';
import SearchBox from '@/components/search/SearchBox';
import PaginationComponent from '@/components/pagination/PaginationComponent';
import {extractErrorMessage} from '@/utils/errorHandling';

interface Role {
    id: number;
    name: string;
    description?: string;
    user_count: number;
    permission_count: number;
    channel_access_count?: number;
    team_access_count?: number;
    restriction_profile_count?: number;
    created_on: string | null;
    updated_on: string | null;
    platform_managed?: boolean;
}

interface ContentItem {
    id: number;
    name: string;
    shortname?: string;
    external_id?: string;
    has_access: boolean;
}

interface ContentAccessData {
    channels: ContentItem[];
    teams: ContentItem[];
}

interface Permission {
    id: number;
    name: string;
    codename: string;
    content_type: string;
    description?: string;
}

interface PaginationInfo {
    page: number;
    per_page: number;
    total: number;
    pages: number;
}

interface User {
    id: number;
    username: string;
    email: string;
    first_name: string;
    last_name: string;
    is_active: boolean;
}

interface RestrictionProfileField {
    field_id: number;
    field_key: string;
    label: string;
    category: string;
}

interface RestrictionProfileItem {
    id: number;
    name: string;
    codename: string;
    description: string | null;
    platform_managed: boolean;
    field_count: number;
    fields?: RestrictionProfileField[];
}

interface RoleRestrictionProfilesData {
    role: {id: number; name: string};
    profiles: RestrictionProfileItem[];
}

interface RoleDetailModalProps {
    modalOpen: boolean;
    closeModal: () => void;
    role: Role;
}

const RoleDetailModal: React.FC<RoleDetailModalProps> = ({modalOpen, closeModal, role}) => {
    const {showSuccess, showError} = useAlert();
    const [activeTab, setActiveTab] = useState<string>('permissions');
    const [loading, setLoading] = useState<boolean>(false);
    const [permissions, setPermissions] = useState<Permission[]>([]);
    const [users, setUsers] = useState<User[]>([]);
    const [availablePermissions, setAvailablePermissions] = useState<Permission[]>([]);
    const [availableUsers, setAvailableUsers] = useState<User[]>([]);
    const [availablePermissionsLoading, setAvailablePermissionsLoading] = useState<boolean>(false);
    // Separate search state for assigned permissions vs available permissions
    const [permissionsSearch, setPermissionsSearch] = useState<string>('');
    const [availablePermissionsSearch, setAvailablePermissionsSearch] = useState<string>('');
    const [userSearch, setUserSearch] = useState<string>('');
    const [selectedPermissions, setSelectedPermissions] = useState<Set<number>>(new Set());
    const [selectedUsers, setSelectedUsers] = useState<Set<number>>(new Set());
    const [permissionsPagination, setPermissionsPagination] = useState<PaginationInfo>({page: 1, per_page: 50, total: 0, pages: 0});
    const [usersPagination, setUsersPagination] = useState<PaginationInfo>({page: 1, per_page: 50, total: 0, pages: 0});
    const [currentPermissionsPage, setCurrentPermissionsPage] = useState<number>(1);
    const [currentUsersPage, setCurrentUsersPage] = useState<number>(1);
    const [currentAvailablePermissionsPage, setCurrentAvailablePermissionsPage] = useState<number>(1);

    // Content Access state
    const [contentData, setContentData] = useState<ContentAccessData | null>(null);
    const [contentLoading, setContentLoading] = useState<boolean>(false);
    const [contentChanges, setContentChanges] = useState<{
        channels: {[key: number]: boolean};
        teams: {[key: number]: boolean};
    }>({
        channels: {},
        teams: {}
    });

    // Restriction Profiles state
    const [roleProfilesData, setRoleProfilesData] = useState<RoleRestrictionProfilesData | null>(null);
    const [profilesLoading, setProfilesLoading] = useState<boolean>(false);

    useEffect(() => {
        if (modalOpen && role) {
            loadRoleDetails();
        } else if (!modalOpen) {
            setContentData(null);
            setContentChanges({channels: {}, teams: {}});
            setRoleProfilesData(null);
            setActiveTab('permissions');
            setAvailablePermissionsLoading(false);
            setPermissionsSearch('');
            setAvailablePermissionsSearch('');
            setUserSearch('');
            setCurrentAvailablePermissionsPage(1);
        }
    }, [modalOpen, role]);

    useEffect(() => {
        if (modalOpen && role) {
            loadPermissions(currentPermissionsPage, permissionsSearch);
        }
    }, [currentPermissionsPage, permissionsSearch]);

    useEffect(() => {
        if (modalOpen && role) {
            loadUsers(currentUsersPage, userSearch);
        }
    }, [currentUsersPage, userSearch]);

    const loadRoleDetails = async (): Promise<void> => {
        setLoading(true);
        try {
            await Promise.all([
                loadPermissions(1, ''),
                loadUsers(1, '')
            ]);
        } catch (error: any) {
            console.error('Failed to load role details:', error);
            const errorMessage = extractErrorMessage(error, 'Failed to load role details');
            showError(i18n.t('role_detail_failed_to_load.text', {error: errorMessage}));
        } finally {
            setLoading(false);
        }
    };

    const loadPermissions = async (page: number = 1, search: string = ''): Promise<void> => {
        try {
            const params = new URLSearchParams({
                page: page.toString(),
                per_page: '50'
            });

            if (search.trim()) {
                params.append('search', search.trim());
            }

            const response = await axios.get(`${buildURL('adminRolePermissions', {role_id: role.id})}?${params}`);

            setPermissions(response.data.permissions || []);
            setPermissionsPagination(response.data.pagination || {page: 1, per_page: 50, total: 0, pages: 0});
        } catch (error: any) {
            console.error('Failed to load permissions:', error);
            showError(i18n.t('role_detail_failed_to_load_permissions.text'));
        }
    };

    const loadUsers = async (page: number = 1, search: string = ''): Promise<void> => {
        try {
            const params = new URLSearchParams({
                page: page.toString(),
                per_page: '50'
            });

            if (search.trim()) {
                params.append('search', search.trim());
            }

            const response = await axios.get(`${buildURL('adminRoleUsers', {role_id: role.id})}?${params}`);

            setUsers(response.data.users || []);
            setUsersPagination(response.data.pagination || {page: 1, per_page: 50, total: 0, pages: 0});
        } catch (error: any) {
            console.error('Failed to load users:', error);
            showError(i18n.t('role_detail_failed_to_load_users.text'));
        }
    };

    const loadAvailablePermissions = async (search: string = ''): Promise<void> => {
        setAvailablePermissionsLoading(true);
        try {
            // Fetch permissions that are available to be added to this role directly from the backend.
            const params = new URLSearchParams({
                available_for_role: String(role.id),
            });

            if (search.trim()) {
                params.append('search', search.trim());
            }

            const response = await axios.get(`${window.URLS.adminPermissionsList}?${params.toString()}`);
            setAvailablePermissions(response.data.permissions || []);
        } catch (error: any) {
            console.error('Failed to load available permissions:', error);
            showError(i18n.t('role_detail_failed_to_load_permissions.text'));
        } finally {
            setAvailablePermissionsLoading(false);
        }
    };

    const loadAvailableUsers = async (): Promise<void> => {
        try {
            const response = await axios.get(window.URLS.adminUsersResource);
            const allUsers = response.data.users || [];
            const currentUserIds = new Set(users.map(u => u.id));
            setAvailableUsers(allUsers.filter((u: User) => !currentUserIds.has(u.id)));
        } catch (error: any) {
            console.error('Failed to load available users:', error);
            showError(i18n.t('role_detail_failed_to_load_users.text'));
        }
    };

    const handleTabChange = (tab: string | null): void => {
        if (tab) {
            setActiveTab(tab);
            if (tab === 'content-access' && !contentData) {
                loadContentAccess();
            }
            if (tab === 'restriction-profiles' && !roleProfilesData) {
                loadRestrictionProfiles();
            }
        }
    };

    const handlePermissionSearchChange = (search: string): void => {
        setPermissionsSearch(search);
        setCurrentPermissionsPage(1);
    };

    const handleAvailablePermissionSearchChange = (search: string): void => {
        setAvailablePermissionsSearch(search);
        setCurrentAvailablePermissionsPage(1);
    };

    const handleUserSearchChange = (search: string): void => {
        setUserSearch(search);
        setCurrentUsersPage(1);
    };

    const handlePermissionPageChange = (page: number): void => {
        setCurrentPermissionsPage(page);
    };

    const handleUserPageChange = (page: number): void => {
        setCurrentUsersPage(page);
    };

    const generatePageArray = (totalPages: number): number[] => {
        return Array.from({length: totalPages}, (_, i) => i + 1);
    };

    // Reload available permissions when the Add permissions tab is active
    // and the search query changes or the modal opens.
    useEffect(() => {
        if (modalOpen && role && activeTab === 'add-permissions') {
            loadAvailablePermissions(availablePermissionsSearch);
        }
    }, [modalOpen, role, activeTab, availablePermissionsSearch]);

    // Backend already applies search filtering for available permissions, we just paginate locally.
    const filteredAvailablePermissions = availablePermissions;

    const availablePerPage = 50;
    const availableTotal = filteredAvailablePermissions.length;
    const availablePages = Math.max(1, Math.ceil(availableTotal / availablePerPage));

    const pagedAvailablePermissions = useMemo(() => {
        const start = (currentAvailablePermissionsPage - 1) * availablePerPage;
        const end = start + availablePerPage;
        return filteredAvailablePermissions.slice(start, end);
    }, [filteredAvailablePermissions, currentAvailablePermissionsPage]);

    const filteredAvailableUsers = useMemo(() => {
        if (!userSearch.trim()) {return availableUsers;}
        const query = userSearch.toLowerCase();
        return availableUsers.filter(u =>
            u.username.toLowerCase().includes(query) ||
            u.email.toLowerCase().includes(query) ||
            u.first_name.toLowerCase().includes(query) ||
            u.last_name.toLowerCase().includes(query)
        );
    }, [availableUsers, userSearch]);

    const handleRemovePermissions = async (): Promise<void> => {
        if (selectedPermissions.size === 0) {return;}

        setLoading(true);
        try {
            await axios.delete(buildURL('adminRolePermissions', {role_id: role.id}), {
                data: {
                    permission_ids: Array.from(selectedPermissions),
                    action: 'remove'
                }
            });

            showSuccess(i18n.t('role_detail_permissions_removed.text', {count: selectedPermissions.size}));
            setSelectedPermissions(new Set());
            await loadPermissions(currentPermissionsPage, permissionsSearch);

            // Refresh available permissions if the add-permissions tab is active
            if (activeTab === 'add-permissions') {
                await loadAvailablePermissions(availablePermissionsSearch);
            }
        } catch (error: any) {
            console.error('Failed to remove permissions:', error);
            const errorMessage = extractErrorMessage(error, 'Failed to remove permissions');
            showError(i18n.t('role_detail_failed_to_remove_permissions.text', {error: errorMessage}));
        } finally {
            setLoading(false);
        }
    };

    const handleAddPermissions = async (): Promise<void> => {
        if (selectedPermissions.size === 0) {return;}

        setLoading(true);
        try {
            await axios.post(buildURL('adminRolePermissions', {role_id: role.id}), {
                permission_ids: Array.from(selectedPermissions),
                action: 'add'
            });

            showSuccess(i18n.t('role_detail_permissions_added.text', {count: selectedPermissions.size}));
            setSelectedPermissions(new Set());
            await loadPermissions(currentPermissionsPage, permissionsSearch);

            // Get fresh permissions data to update available permissions
            await loadAvailablePermissions(availablePermissionsSearch);
        } catch (error: any) {
            console.error('Failed to add permissions:', error);
            const errorMessage = extractErrorMessage(error, 'Failed to add permissions');
            showError(i18n.t('role_detail_failed_to_add_permissions.text', {error: errorMessage}));
        } finally {
            setLoading(false);
        }
    };

    const handleRemoveUsers = async (): Promise<void> => {
        if (selectedUsers.size === 0) {return;}

        setLoading(true);
        try {
            await axios.delete(buildURL('adminRoleUsers', {role_id: role.id}), {
                data: {
                    user_ids: Array.from(selectedUsers),
                    action: 'remove'
                }
            });

            showSuccess(i18n.t('role_detail_users_removed.text', {count: selectedUsers.size}));
            setSelectedUsers(new Set());
            await loadUsers(currentUsersPage, userSearch);
        } catch (error: any) {
            console.error('Failed to remove users:', error);
            const errorMessage = extractErrorMessage(error, 'Failed to remove users');
            showError(i18n.t('role_detail_failed_to_remove_users.text', {error: errorMessage}));
        } finally {
            setLoading(false);
        }
    };

    const handleAddUsers = async (): Promise<void> => {
        if (selectedUsers.size === 0) {return;}

        setLoading(true);
        try {
            await axios.post(buildURL('adminRoleUsers', {role_id: role.id}), {
                user_ids: Array.from(selectedUsers),
                action: 'add'
            });

            showSuccess(i18n.t('role_detail_users_added.text', {count: selectedUsers.size}));
            setSelectedUsers(new Set());
            await loadUsers(currentUsersPage, userSearch);
        } catch (error: any) {
            console.error('Failed to add users:', error);
            const errorMessage = extractErrorMessage(error, 'Failed to add users');
            showError(i18n.t('role_detail_failed_to_add_users.text', {error: errorMessage}));
        } finally {
            setLoading(false);
        }
    };

    // Content Access functions
    const loadContentAccess = async (): Promise<void> => {
        setContentLoading(true);
        try {
            const response = await axios.get(buildURL('adminRoleContentAccess', {role_id: role.id}));
            setContentData(response.data);
            setContentChanges({channels: {}, teams: {}});
        } catch (error: any) {
            console.error('Failed to load content access:', error);
            showError(i18n.t('content_access_failed_to_load.text'));
        } finally {
            setContentLoading(false);
        }
    };

    const loadRestrictionProfiles = async (): Promise<void> => {
        setProfilesLoading(true);
        try {
            const response = await axios.get(buildURL('adminRoleRestrictionProfiles', {role_id: role.id}));
            setRoleProfilesData(response.data);
        } catch (error: any) {
            console.error('Failed to load restriction profiles:', error);
            showError(i18n.t('role_detail_failed_to_load_profiles.text', {defaultValue: 'Failed to load restriction profiles'}));
        } finally {
            setProfilesLoading(false);
        }
    };

    const handleContentAccessToggle = (contentType: 'channels' | 'teams', itemId: number, hasAccess: boolean): void => {
        setContentChanges(prev => ({
            ...prev,
            [contentType]: {
                ...prev[contentType],
                [itemId]: hasAccess
            }
        }));
    };

    const getEffectiveAccess = (contentType: 'channels' | 'teams', itemId: number, originalAccess: boolean): boolean => {
        return contentChanges[contentType][itemId] !== undefined ? contentChanges[contentType][itemId] : originalAccess;
    };

    const hasContentChanges = (): boolean => {
        return Object.keys(contentChanges.channels).length > 0 ||
            Object.keys(contentChanges.teams).length > 0;
    };

    const saveContentChanges = async (): Promise<void> => {
        if (!hasContentChanges()) {return;}

        setContentLoading(true);
        try {
            // Process changes for each content type
            for (const [contentType, typeChanges] of Object.entries(contentChanges)) {
                if (Object.keys(typeChanges).length === 0) {continue;}

                // Group by grant/revoke action
                const toGrant: number[] = [];
                const toRevoke: number[] = [];

                Object.entries(typeChanges).forEach(([itemId, hasAccess]) => {
                    if (hasAccess) {
                        toGrant.push(parseInt(itemId));
                    } else {
                        toRevoke.push(parseInt(itemId));
                    }
                });

                // Send grant requests
                if (toGrant.length > 0) {
                    await axios.post(buildURL('adminRoleContentAccess', {role_id: role.id}), {
                        content_type: contentType.slice(0, -1), // Remove 's' from plural
                        object_ids: toGrant,
                        action: 'grant'
                    });
                }

                // Send revoke requests
                if (toRevoke.length > 0) {
                    await axios.post(buildURL('adminRoleContentAccess', {role_id: role.id}), {
                        content_type: contentType.slice(0, -1), // Remove 's' from plural
                        object_ids: toRevoke,
                        action: 'revoke'
                    });
                }
            }

            showSuccess(i18n.t('content_access_updated_successfully.text'));
            setContentChanges({channels: {}, teams: {}});
            await loadContentAccess(); // Reload to get fresh data
        } catch (error: any) {
            console.error('Failed to save content changes:', error);
            showError(i18n.t('content_access_failed_to_update.text'));
        } finally {
            setContentLoading(false);
        }
    };

    const basePermissionColumns = [
        {
            header: i18n.t('role_detail_permission_header.text'),
            accessorKey: 'name',
        },
        {
            header: i18n.t('role_detail_code_header.text'),
            accessorKey: 'codename',
        },
    ];

    const selectPermissionColumn = {
        id: 'select',
        header: '',
        cell: ({row}: any) => (
            <div className="form-check">
                <input
                    className="form-check-input"
                    type="checkbox"
                    checked={selectedPermissions.has(row.original.id)}
                    onChange={(e) => {
                        const newSelected = new Set(selectedPermissions);
                        if (e.target.checked) {
                            newSelected.add(row.original.id);
                        } else {
                            newSelected.delete(row.original.id);
                        }
                        setSelectedPermissions(newSelected);
                    }}
                    style={{position: 'relative', zIndex: 1}}
                />
            </div>
        ),
    };

    const permissionColumns = role.platform_managed
        ? basePermissionColumns
        : [selectPermissionColumn, ...basePermissionColumns];

    const baseUserColumns = [
        {
            header: i18n.t('role_detail_username_header.text'),
            accessorKey: 'username',
        },
        {
            header: i18n.t('role_detail_name_header.text'),
            accessorFn: (row: User) => `${row.first_name} ${row.last_name}`.trim() || '-',
        },
        {
            header: i18n.t('role_detail_email_header.text'),
            accessorKey: 'email',
        },
        {
            header: i18n.t('role_detail_status_header.text'),
            accessorKey: 'is_active',
            cell: ({getValue}: {getValue: () => boolean}) => (
                <Badge variant={getValue() ? 'success' : 'danger'}>
                    {getValue() ? i18n.t('role_detail_active.text') : i18n.t('role_detail_inactive.text')}
                </Badge>
            ),
        },
    ];

    const selectUserColumn = {
        id: 'select',
        header: '',
        cell: ({row}: any) => (
            <div className="form-check">
                <input
                    className="form-check-input"
                    type="checkbox"
                    checked={selectedUsers.has(row.original.id)}
                    onChange={(e) => {
                        const newSelected = new Set(selectedUsers);
                        if (e.target.checked) {
                            newSelected.add(row.original.id);
                        } else {
                            newSelected.delete(row.original.id);
                        }
                        setSelectedUsers(newSelected);
                    }}
                    style={{position: 'relative', zIndex: 1}}
                />
            </div>
        ),
    };

    const userColumns = role.platform_managed
        ? baseUserColumns
        : [selectUserColumn, ...baseUserColumns];

    return (
        <Modal show={modalOpen} onHide={closeModal} size="xl" style={{maxHeight: '90vh'}}>
            <Modal.Header closeButton>
                <Modal.Title>
                    {i18n.t('role_detail_title.text', {roleName: role.name})}
                    {role.platform_managed && <Badge variant="secondary" className="ml-2">{i18n.t('roles_system_badge.text')}</Badge>}
                </Modal.Title>
            </Modal.Header>

            <Modal.Body style={{maxHeight: '70vh', overflowY: 'auto'}}>
                {role.description && (
                    <div className="alert alert-info mb-3">
                        <strong>{i18n.t('roles_description.text')}:</strong> {role.description}
                    </div>
                )}

                <Row className="mb-3">
                    <Col md={4}>
                        <div className="card text-center">
                            <div className="card-body py-2">
                                <div className="fw-bold text-primary">{role.permission_count || 0}</div>
                                <small className="text-muted">{i18n.t('role_detail_permissions_label.text')}</small>
                            </div>
                        </div>
                    </Col>
                    <Col md={4}>
                        <div className="card text-center">
                            <div className="card-body py-2">
                                <div className="fw-bold text-success">{role.channel_access_count || 0}</div>
                                <small className="text-muted">{i18n.t('role_detail_channels_label.text')}</small>
                            </div>
                        </div>
                    </Col>
                    <Col md={4}>
                        <div className="card text-center">
                            <div className="card-body py-2">
                                <div className="fw-bold text-warning">{role.team_access_count || 0}</div>
                                <small className="text-muted">{i18n.t('role_detail_teams_label.text')}</small>
                            </div>
                        </div>
                    </Col>
                </Row>

                <Tabs activeKey={activeTab} onSelect={handleTabChange} className="mb-3">
                    <Tab eventKey="permissions" title={i18n.t('role_detail_permissions_tab.text', {count: permissionsPagination.total})}>
                        <Row className="mb-3">
                            <Col md={6}>
                                <SearchBox
                                    value={permissionsSearch}
                                    onChange={handlePermissionSearchChange}
                                    placeholder={i18n.t('role_detail_search_permissions.text')}
                                />
                            </Col>
                            <Col md={6} className="d-flex justify-content-end">
                                {selectedPermissions.size > 0 && !role.platform_managed && (
                                    <Button
                                        variant="danger"
                                        size="sm"
                                        icon="mdi-minus"
                                        onClick={handleRemovePermissions}
                                        loading={loading}
                                    >
                                        {i18n.t('role_detail_remove_selected.text', {count: selectedPermissions.size})}
                                    </Button>
                                )}
                            </Col>
                        </Row>

                        {loading ? (
                            <Spinner text={i18n.t('sp_loading.text')} centered />
                        ) : (
                            <>
                                <div className="card">
                                    <div className="card-body">
                                        <TableComponent
                                            data={permissions}
                                            columns={permissionColumns}
                                        />

                                        {permissionsPagination.pages > 1 && (
                                            <PaginationComponent
                                                pages={generatePageArray(permissionsPagination.pages)}
                                                currentPage={permissionsPagination.page}
                                                onPageChange={handlePermissionPageChange}
                                                centered={true}
                                            />
                                        )}
                                    </div>
                                </div>
                            </>
                        )}
                    </Tab>

                    <Tab eventKey="users" title={i18n.t('role_detail_users_tab.text', {count: usersPagination.total})}>
                        <Row className="mb-3">
                            <Col md={6}>
                                <SearchBox
                                    value={userSearch}
                                    onChange={handleUserSearchChange}
                                    placeholder={i18n.t('role_detail_search_users.text')}
                                />
                            </Col>
                            <Col md={6} className="d-flex justify-content-end">
                                {selectedUsers.size > 0 && !role.platform_managed && (
                                    <Button
                                        variant="danger"
                                        size="sm"
                                        icon="mdi-minus"
                                        onClick={handleRemoveUsers}
                                        loading={loading}
                                    >
                                        {i18n.t('role_detail_remove_selected.text', {count: selectedUsers.size})}
                                    </Button>
                                )}
                            </Col>
                        </Row>

                        {loading ? (
                            <Spinner text={i18n.t('sp_loading.text')} centered />
                        ) : (
                            <>
                                <div className="card">
                                    <div className="card-body">
                                        <TableComponent
                                            data={users}
                                            columns={userColumns}
                                        />
                                        {usersPagination.pages > 1 && (
                                            <PaginationComponent
                                                pages={generatePageArray(usersPagination.pages)}
                                                currentPage={usersPagination.page}
                                                onPageChange={handleUserPageChange}
                                                centered={true}
                                            />
                                        )}
                                    </div>
                                </div>
                            </>
                        )}
                    </Tab>

                    <Tab eventKey="content-access" title={i18n.t('role_detail_content_access.text', {count: (role.channel_access_count || 0) + (role.team_access_count || 0)})}>
                        <div style={{maxHeight: 'calc(70vh - 200px)', overflowY: 'auto'}}>
                            {contentLoading ? (
                                <Spinner text={i18n.t('content_access_loading.text')} centered />
                            ) : contentData ? (
                                <>
                                    <div className="mb-3">
                                        <p className="text-muted">
                                            {i18n.t('content_access_manage_description.text')}
                                            {hasContentChanges() && (
                                                <span className="text-warning ml-2">
                                                    <Icon name="mdi-timeline-outline" className="mr-1" />
                                                    {i18n.t('content_access_unsaved_changes.text')}
                                                </span>
                                            )}
                                        </p>
                                        {hasContentChanges() && !role.platform_managed && (
                                            <div className="mb-3 d-flex">
                                                <Button
                                                    variant="primary"
                                                    size="sm"
                                                    icon="mdi-content-save"
                                                    onClick={saveContentChanges}
                                                    loading={contentLoading}
                                                    className="mr-3"
                                                >
                                                    {i18n.t('content_access_save_changes.text')}
                                                </Button>
                                                <Button
                                                    variant="secondary"
                                                    size="sm"
                                                    onClick={() => setContentChanges({channels: {}, teams: {}})}
                                                >
                                                    {i18n.t('content_access_cancel_changes.text')}
                                                </Button>
                                            </div>
                                        )}
                                    </div>

                                    <Row className="g-3">
                                        <Col md={6}>
                                            <div className="card">
                                                <div className="card-header">
                                                    <Icon name="mdi-chat" className="mr-2" />
                                                    {i18n.t('role_detail_channels_label.text')} ({contentData.channels.length})
                                                </div>
                                                <div className="card-body" style={{maxHeight: '300px', overflowY: 'auto', minHeight: '150px', position: 'relative'}}>
                                                    {contentData.channels.map(channel => {
                                                        const effectiveAccess = getEffectiveAccess('channels', channel.id, channel.has_access);
                                                        const hasChanged = contentChanges.channels[channel.id] !== undefined && contentChanges.channels[channel.id] !== channel.has_access;

                                                        return (
                                                            <div key={channel.id} className="d-flex justify-content-between align-items-center py-2 border-bottom" style={{position: 'relative', zIndex: 1}}>
                                                                <div className="flex-grow-1 mr-3">
                                                                    <div className="fw-bold">{channel.name}</div>
                                                                    {channel.shortname && (
                                                                        <small className="text-muted">({channel.shortname})</small>
                                                                    )}
                                                                </div>
                                                                <div className="d-flex align-items-center gap-2" style={{flexShrink: 0}}>
                                                                    {hasChanged && (
                                                                        <Badge variant="warning" className="px-2 py-1 mr-2">
                                                                            <Icon name="mdi-timeline-outline" className="mr-1" />
                                                                            {i18n.t('role_detail_pending_badge.text')}
                                                                        </Badge>
                                                                    )}
                                                                    <div className="form-check form-switch">
                                                                        <input
                                                                            className="form-check-input"
                                                                            type="checkbox"
                                                                            id={`channel-${channel.id}`}
                                                                            checked={effectiveAccess}
                                                                            onChange={(e) => handleContentAccessToggle('channels', channel.id, e.target.checked)}
                                                                            disabled={role.platform_managed}
                                                                            style={{position: 'relative', zIndex: 2}}
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                    {contentData.channels.length === 0 && (
                                                        <p className="text-muted text-center py-3">{i18n.t('content_access_no_channels.text')}</p>
                                                    )}
                                                </div>
                                            </div>
                                        </Col>

                                        <Col md={6}>
                                            <div className="card">
                                                <div className="card-header">
                                                    <Icon name="mdi-account-group" className="mr-2" />
                                                    {i18n.t('role_detail_teams_label.text')} ({contentData.teams.length})
                                                </div>
                                                <div className="card-body" style={{maxHeight: '300px', overflowY: 'auto', minHeight: '150px', position: 'relative'}}>
                                                    {contentData.teams.map(team => {
                                                        const effectiveAccess = getEffectiveAccess('teams', team.id, team.has_access);
                                                        const hasChanged = contentChanges.teams[team.id] !== undefined && contentChanges.teams[team.id] !== team.has_access;

                                                        return (
                                                            <div key={team.id} className="d-flex justify-content-between align-items-center py-2 border-bottom" style={{position: 'relative', zIndex: 1}}>
                                                                <div className="flex-grow-1 mr-3">
                                                                    <div className="fw-bold">{team.name}</div>
                                                                    {team.shortname && (
                                                                        <small className="text-muted">({team.shortname})</small>
                                                                    )}
                                                                </div>
                                                                <div className="d-flex align-items-center gap-2" style={{flexShrink: 0}}>
                                                                    {hasChanged && (
                                                                        <Badge variant="warning" className="px-2 py-1 mr-2">
                                                                            <Icon name="mdi-timeline-outline" className="mr-1" />
                                                                            {i18n.t('role_detail_pending_badge.text')}
                                                                        </Badge>
                                                                    )}
                                                                    <div className="form-check form-switch">
                                                                        <input
                                                                            className="form-check-input"
                                                                            type="checkbox"
                                                                            id={`team-${team.id}`}
                                                                            checked={effectiveAccess}
                                                                            onChange={(e) => handleContentAccessToggle('teams', team.id, e.target.checked)}
                                                                            disabled={role.platform_managed}
                                                                            style={{position: 'relative', zIndex: 2}}
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                    {contentData.teams.length === 0 && (
                                                        <p className="text-muted text-center py-3">{i18n.t('content_access_no_teams.text')}</p>
                                                    )}
                                                </div>
                                            </div>
                                        </Col>

                                    </Row>
                                </>
                            ) : (
                                <div className="alert alert-warning">
                                    {i18n.t('content_access_failed_to_refresh.text')}
                                </div>
                            )}
                        </div>
                    </Tab>

                    <Tab eventKey="restriction-profiles" title={i18n.t('role_detail_restriction_profiles_tab.text', {defaultValue: 'Restriction Profiles ({{count}})', count: roleProfilesData?.profiles.length ?? role.restriction_profile_count ?? 0})}>
                        <p className="text-muted mb-3">
                            {i18n.t('role_detail_profiles_readonly_description.text', {defaultValue: 'Restriction profiles assigned to this role. Manage assignments from the Restriction Profiles tab.'})}
                        </p>

                        {profilesLoading ? (
                            <Spinner text={i18n.t('sp_loading.text')} centered />
                        ) : (
                            <div className="card">
                                <div className="card-body">
                                    {!roleProfilesData || roleProfilesData.profiles.length === 0 ? (
                                        <p className="text-muted text-center py-3">
                                            {i18n.t('role_detail_no_profiles_assigned.text', {defaultValue: 'No restriction profiles assigned to this role'})}
                                        </p>
                                    ) : (
                                        <div className="restriction-profiles-table">
                                        <TableComponent
                                            data={roleProfilesData.profiles}
                                            customTable={true}
                                            columns={[
                                                {
                                                    header: i18n.t('jt_name.text', {defaultValue: 'Name'}),
                                                    accessorKey: 'name',
                                                    size: 110,
                                                    cell: ({row}: any) => (
                                                        <div>
                                                            <div>{row.original.name}</div>
                                                            {row.original.platform_managed && (
                                                                <Badge variant="secondary" size="sm" spacing="none">{i18n.t('roles_system_badge.text')}</Badge>
                                                            )}
                                                        </div>
                                                    ),
                                                },
                                                {
                                                    header: i18n.t('jt_description.text', {defaultValue: 'Description'}),
                                                    accessorKey: 'description',
                                                    size: 220,
                                                    cell: ({getValue}: {getValue: () => string}) => (
                                                        <span className="text-muted">{getValue() || '-'}</span>
                                                    ),
                                                },
                                                {
                                                    header: i18n.t('restriction_profiles_fields_header.text', {defaultValue: 'Fields'}),
                                                    accessorKey: 'fields',
                                                    cell: ({row}: any) => {
                                                        const fields = row.original.fields || [];
                                                        if (fields.length === 0) return <span className="text-muted">-</span>;
                                                        const categoryColors: Record<string, string> = {
                                                            pii: '#5b6abf',
                                                        };
                                                        return (
                                                            <div style={{display: 'flex', flexWrap: 'wrap', gap: '4px'}}>
                                                                {fields.map((f: RestrictionProfileField) => (
                                                                    <span
                                                                        key={f.field_id}
                                                                        style={{
                                                                            display: 'inline-block',
                                                                            fontSize: '11px',
                                                                            padding: '2px 8px',
                                                                            borderRadius: '12px',
                                                                            backgroundColor: (categoryColors[f.category] || '#6c757d') + '1A',
                                                                            color: categoryColors[f.category] || '#6c757d',
                                                                            border: `1px solid ${(categoryColors[f.category] || '#6c757d')}40`,
                                                                        }}
                                                                    >
                                                                        {f.label}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        );
                                                    },
                                                },
                                            ]}
                                        />
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </Tab>

                    {!role.platform_managed && (
                        <Tab eventKey="add-permissions" title={i18n.t('role_detail_add_permissions_tab.text')}>
                            <Row className="mb-3">
                                <Col md={6}>
                                    <SearchBox
                                        value={availablePermissionsSearch}
                                        onChange={handleAvailablePermissionSearchChange}
                                        placeholder={i18n.t('role_detail_search_available_permissions.text')}
                                    />
                                </Col>
                                <Col md={6} className="d-flex justify-content-end">
                                    {selectedPermissions.size > 0 && (
                                        <Button
                                            variant="success"
                                            size="sm"
                                            icon="mdi-plus"
                                            onClick={handleAddPermissions}
                                            loading={loading}
                                        >
                                            {i18n.t('role_detail_add_selected.text', {count: selectedPermissions.size})}
                                        </Button>
                                    )}
                                </Col>
                            </Row>

                            {availablePermissionsLoading ? (
                                <Spinner text={i18n.t('sp_loading.text')} centered />
                            ) : (
                                <div className="card">
                                    <div className="card-body">
                                        <TableComponent
                                            data={pagedAvailablePermissions}
                                            columns={permissionColumns}
                                        />
                                        {availablePages > 1 && (
                                            <PaginationComponent
                                                pages={generatePageArray(availablePages)}
                                                currentPage={currentAvailablePermissionsPage}
                                                onPageChange={setCurrentAvailablePermissionsPage}
                                                centered={true}
                                            />
                                        )}
                                    </div>
                                </div>
                            )}
                        </Tab>
                    )}

                </Tabs>

                {role.platform_managed && (
                    <div className="alert alert-warning">
                        <Icon name="mdi-information" className="mr-2 text-info" />
                        <strong>{i18n.t('role_detail_system_role_label.text')}:</strong> {i18n.t('role_detail_system_role_description.text')}
                    </div>
                )}
            </Modal.Body>

            <Modal.Footer>
                <Button variant="secondary" onClick={closeModal}>
                    {i18n.t('jt_close.text')}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default RoleDetailModal;
